import { en, pl } from "@acme/i18n";
import type { RouteRecordNameGeneric } from "vue-router";

type SupportedLocale = "pl" | "en";
type SeoRoute = {
  name: RouteRecordNameGeneric | null | undefined;
  path: string;
};
type RouteSeoKey =
  | "home"
  | "order"
  | "terms"
  | "privacy"
  | "deliveries"
  | "thankyou"
  | "orderStatus"
  | "notFound";

type SeoEntry = {
  title: string;
  description: string;
  path: string;
  robots?: string;
};

type SeoState = SeoEntry & {
  canonicalUrl: string;
  image: string;
  locale: SupportedLocale;
  ogLocale: string;
  structuredData: {
    "@context": "https://schema.org";
    "@graph": unknown[];
  };
};

const SITE_URL = "https://borowkaszczecin.pl";
const SITE_NAME = "BorowkaSzczecin";
const DEFAULT_IMAGE = `${SITE_URL}/logogrube.png`;

const SERVICE_AREAS = [
  "Szczecin",
  "Prawobrzeże",
  "Szczecin Dąbie",
  "Wielgowo",
  "Police",
  "Pilchowo",
  "Tanowo",
  "Bartoszewo",
  "Sławoszewo",
  "Kołbaskowo",
  "Przecław",
  "Tatynia",
  "Kościno",
  "Dołuje",
  "Wołczkowo",
  "Przęsocin",
  "Mścięcino",
  "Mierzyn",
  "Dobra",
];

const seoByRoute: Record<RouteSeoKey, Record<SupportedLocale, SeoEntry>> = {
  home: {
    pl: {
      title: "BorowkaSzczecin - Świeże borówki amerykańskie w Szczecinie",
      description:
        "Świeże borówki amerykańskie z rodzinnej plantacji w Pilchowie. Zamów online z dostawą w Szczecinie i powiecie polickim albo odbierz owoce na plantacji.",
      path: "/",
    },
    en: {
      title: "BorowkaSzczecin - fresh blueberries in Szczecin",
      description:
        "Fresh highbush blueberries from a family plantation in Pilchowo. Order online with delivery in Szczecin and Police county or collect at the plantation.",
      path: "/",
    },
  },
  order: {
    pl: {
      title: "Zamów świeże borówki - BorowkaSzczecin",
      description:
        "Złóż zamówienie na świeże borówki z dostawą w Szczecinie lub odbiorem osobistym na plantacji w Pilchowie.",
      path: "/zamowienia/",
      robots: "noindex, follow",
    },
    en: {
      title: "Order fresh blueberries - BorowkaSzczecin",
      description:
        "Order fresh blueberries with delivery in Szczecin or pickup at the Pilchowo plantation.",
      path: "/zamowienia/",
      robots: "noindex, follow",
    },
  },
  terms: {
    pl: {
      title: "Regulamin sklepu - BorowkaSzczecin",
      description:
        "Regulamin sklepu internetowego Gospodarstwa Rolnego Mateusz Wdowiak, właściciela BorowkaSzczecin.",
      path: "/regulamin/",
    },
    en: {
      title: "Terms and conditions - BorowkaSzczecin",
      description:
        "Terms of service for the online store of Agricultural Farm Mateusz Wdowiak, owner of BorowkaSzczecin.",
      path: "/regulamin/",
    },
  },
  privacy: {
    pl: {
      title: "Polityka prywatności - BorowkaSzczecin",
      description:
        "Zasady przetwarzania danych osobowych w sklepie internetowym borowkaszczecin.pl.",
      path: "/polityka-prywatnosci/",
    },
    en: {
      title: "Privacy policy - BorowkaSzczecin",
      description:
        "Personal data processing rules for the borowkaszczecin.pl online store.",
      path: "/polityka-prywatnosci/",
    },
  },
  deliveries: {
    pl: {
      title: "Dostawa borówek w Szczecinie - BorowkaSzczecin",
      description:
        "Sprawdź obszar dostawy świeżych borówek w Szczecinie, Pilchowie, Policach i okolicach.",
      path: "/dostawy-informacje/",
    },
    en: {
      title: "Blueberry delivery in Szczecin - BorowkaSzczecin",
      description:
        "Check the fresh blueberry delivery area in Szczecin, Pilchowo, Police and nearby locations.",
      path: "/dostawy-informacje/",
    },
  },
  thankyou: {
    pl: {
      title: "Dziękujemy za zamówienie - BorowkaSzczecin",
      description:
        "Potwierdzenie przyjęcia zamówienia w sklepie BorowkaSzczecin.",
      path: "/podziekowanie/",
      robots: "noindex, follow",
    },
    en: {
      title: "Thank you for your order - BorowkaSzczecin",
      description: "Order confirmation for BorowkaSzczecin.",
      path: "/podziekowanie/",
      robots: "noindex, follow",
    },
  },
  orderStatus: {
    pl: {
      title: "Status zamówienia - BorowkaSzczecin",
      description: "Sprawdź aktualny status swojego zamówienia w BorowkaSzczecin.",
      path: "/status-zamowienia/",
      robots: "noindex, follow",
    },
    en: {
      title: "Order status - BorowkaSzczecin",
      description: "Check the current status of your BorowkaSzczecin order.",
      path: "/status-zamowienia/",
      robots: "noindex, follow",
    },
  },
  notFound: {
    pl: {
      title: "Nie znaleziono strony - BorowkaSzczecin",
      description: "Strona, której szukasz, nie istnieje albo została przeniesiona.",
      path: "/",
      robots: "noindex, follow",
    },
    en: {
      title: "Page not found - BorowkaSzczecin",
      description: "The page you are looking for does not exist or has been moved.",
      path: "/",
      robots: "noindex, follow",
    },
  },
};

const routeNameToSeoKey: Record<string, RouteSeoKey> = {
  home: "home",
  order: "order",
  terms: "terms",
  privacy: "privacy",
  deliveries: "deliveries",
  thankyou: "thankyou",
  orderStatus: "orderStatus",
  NotFound: "notFound",
};

export function applySeo(route: SeoRoute, localeValue: string, price?: number) {
  if (typeof document === "undefined") return;

  const seo = getSeoState(route, localeValue, price);

  document.documentElement.lang = seo.locale;
  document.title = seo.title;

  setMetaName("description", seo.description);
  setMetaName("robots", seo.robots ?? "index, follow");
  setMetaName("author", "Gospodarstwo Rolne Mateusz Wdowiak");
  setMetaName("theme-color", "#3150a8");

  setCanonical(seo.canonicalUrl);

  setMetaProperty("og:site_name", SITE_NAME);
  setMetaProperty("og:type", "website");
  setMetaProperty("og:title", seo.title);
  setMetaProperty("og:description", seo.description);
  setMetaProperty("og:url", seo.canonicalUrl);
  setMetaProperty("og:image", seo.image);
  setMetaProperty("og:image:alt", "Logo BorowkaSzczecin");
  setMetaProperty("og:locale", seo.ogLocale);

  setMetaName("twitter:card", "summary_large_image");
  setMetaName("twitter:title", seo.title);
  setMetaName("twitter:description", seo.description);
  setMetaName("twitter:image", seo.image);
  setMetaName("twitter:image:alt", "Logo BorowkaSzczecin");

  setStructuredData(seo.structuredData);
}

export function renderSeoHead(route: SeoRoute, localeValue: string, price?: number) {
  const seo = getSeoState(route, localeValue, price);
  const structuredData = JSON.stringify(seo.structuredData).replace(
    /</g,
    "\\u003c",
  );

  return [
    `<title>${escapeHtml(seo.title)}</title>`,
    `<meta name="description" content="${escapeHtml(seo.description)}" />`,
    `<meta name="robots" content="${escapeHtml(seo.robots ?? "index, follow")}" />`,
    `<meta name="theme-color" content="#3150a8" />`,
    `<meta name="author" content="Gospodarstwo Rolne Mateusz Wdowiak" />`,
    `<link rel="canonical" href="${escapeHtml(seo.canonicalUrl)}" />`,
    `<meta property="og:site_name" content="${SITE_NAME}" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:title" content="${escapeHtml(seo.title)}" />`,
    `<meta property="og:description" content="${escapeHtml(seo.description)}" />`,
    `<meta property="og:url" content="${escapeHtml(seo.canonicalUrl)}" />`,
    `<meta property="og:image" content="${escapeHtml(seo.image)}" />`,
    `<meta property="og:image:alt" content="Logo BorowkaSzczecin" />`,
    `<meta property="og:locale" content="${seo.ogLocale}" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${escapeHtml(seo.title)}" />`,
    `<meta name="twitter:description" content="${escapeHtml(seo.description)}" />`,
    `<meta name="twitter:image" content="${escapeHtml(seo.image)}" />`,
    `<meta name="twitter:image:alt" content="Logo BorowkaSzczecin" />`,
    `<script id="structured-data" type="application/ld+json">${structuredData}</script>`,
  ].join("\n    ");
}

function getSeoState(route: SeoRoute, localeValue: string, price?: number): SeoState {
  const locale = normalizeLocale(localeValue);
  const key = getSeoKey(route);
  const seo = seoByRoute[key][locale];
  const canonicalPath = key === "notFound" ? route.path : seo.path;
  const canonicalUrl = toAbsoluteUrl(canonicalPath);

  return {
    ...seo,
    canonicalUrl,
    image: DEFAULT_IMAGE,
    locale,
    ogLocale: locale === "pl" ? "pl_PL" : "en_US",
    structuredData: getStructuredData(key, locale, canonicalUrl, price),
  };
}

function getSeoKey(route: SeoRoute): RouteSeoKey {
  return routeNameToSeoKey[String(route.name)] ?? "notFound";
}

function normalizeLocale(locale: string): SupportedLocale {
  return locale === "en" ? "en" : "pl";
}

function toAbsoluteUrl(path: string) {
  if (!path || path === "/") return `${SITE_URL}/`;

  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${cleanPath}`;
}

function setMetaName(name: string, content: string) {
  const element = getOrCreateMeta(`meta[name="${name}"]`);

  element.setAttribute("name", name);
  element.setAttribute("content", content);
}

function setMetaProperty(property: string, content: string) {
  const element = getOrCreateMeta(`meta[property="${property}"]`);

  element.setAttribute("property", property);
  element.setAttribute("content", content);
}

function getOrCreateMeta(selector: string) {
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  if (existing) return existing;

  const element = document.createElement("meta");
  document.head.appendChild(element);

  return element;
}

function setCanonical(href: string) {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.appendChild(element);
  }

  element.href = href;
}

function setStructuredData(structuredData: SeoState["structuredData"]) {
  let element = document.getElementById("structured-data") as HTMLScriptElement | null;

  if (!element) {
    element = document.createElement("script");
    element.id = "structured-data";
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(
    structuredData,
    null,
    2,
  );
}

function getStructuredData(
  routeKey: RouteSeoKey,
  locale: SupportedLocale,
  url: string,
  price?: number,
) {
  const graph: unknown[] = [getBusinessSchema(locale), getWebsiteSchema(locale)];

  if (routeKey === "home") {
    graph.push(getFaqSchema(locale));

    if (typeof price === "number" && Number.isFinite(price)) {
      graph.push(getProductSchema(locale, price));
    }
  }

  if (!["home", "order", "thankyou", "orderStatus", "notFound"].includes(routeKey)) {
    graph.push(getBreadcrumbSchema(routeKey, locale, url));
  }

  return {
    "@context": "https://schema.org" as const,
    "@graph": graph,
  };
}

function escapeHtml(value: string) {
  return value.replace(/[&<>"']/g, (character) => {
    switch (character) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      case "'":
        return "&#39;";
      default:
        return character;
    }
  });
}

function getBusinessSchema(locale: SupportedLocale) {
  return {
    "@type": ["LocalBusiness", "Store"],
    "@id": `${SITE_URL}/#business`,
    name: SITE_NAME,
    alternateName: "Borówka Szczecin",
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/borowki.png`,
    image: DEFAULT_IMAGE,
    telephone: "+48790278445",
    email: "kontakt@borowkaszczecin.pl",
    description:
      locale === "pl"
        ? "Rodzinna plantacja świeżych borówek amerykańskich w Pilchowie z dostawą w Szczecinie i powiecie polickim."
        : "Family plantation of fresh highbush blueberries in Pilchowo with delivery in Szczecin and Police county.",
    address: {
      "@type": "PostalAddress",
      streetAddress: "ul. Piaskowa 5c/7",
      postalCode: "72-010",
      addressLocality: "Police",
      addressCountry: "PL",
    },
    areaServed: SERVICE_AREAS,
    sameAs: [
      "https://www.facebook.com/borowka.szczecin",
      "https://www.instagram.com/borowka.szczecin/",
    ],
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        opens: "09:00",
        closes: "19:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: "Sunday",
        opens: "10:00",
        closes: "19:00",
      },
    ],
  };
}

function getWebsiteSchema(locale: SupportedLocale) {
  return {
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    inLanguage: locale,
    publisher: {
      "@id": `${SITE_URL}/#business`,
    },
  };
}

function getProductSchema(locale: SupportedLocale, price: number) {
  return {
    "@type": "Product",
    "@id": `${SITE_URL}/#blueberries`,
    name: locale === "pl" ? "Świeże borówki amerykańskie" : "Fresh highbush blueberries",
    description:
      locale === "pl"
        ? "Świeże, ręcznie zbierane borówki amerykańskie pakowane w kilogramowe kartonowe pudełka."
        : "Fresh, hand-picked highbush blueberries packed in one-kilogram cardboard boxes.",
    image: DEFAULT_IMAGE,
    category: locale === "pl" ? "Owoce sezonowe" : "Seasonal fruit",
    brand: {
      "@id": `${SITE_URL}/#business`,
    },
    offers: {
      "@type": "Offer",
      price: price.toFixed(2),
      priceCurrency: "PLN",
      availability: "https://schema.org/InStock",
      url: `${SITE_URL}/`,
      seller: {
        "@id": `${SITE_URL}/#business`,
      },
    },
    areaServed: ["Szczecin", "Pilchowo", "Police", "powiat policki"],
  };
}

function getFaqSchema(locale: SupportedLocale) {
  const messages = locale === "pl" ? pl : en;
  const dynamicPriceQuestion =
    locale === "pl" ? "Jakie są ceny borówek?" : "What are the blueberry prices?";
  const entities = messages.faq.items
    .filter((item) => item.question !== dynamicPriceQuestion)
    .map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    }));

  return {
    "@type": "FAQPage",
    "@id": `${SITE_URL}/#faq`,
    mainEntity: entities,
  };
}

function getBreadcrumbSchema(routeKey: RouteSeoKey, locale: SupportedLocale, url: string) {
  const page = seoByRoute[routeKey][locale];

  return {
    "@type": "BreadcrumbList",
    "@id": `${url}#breadcrumb`,
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: locale === "pl" ? "Strona główna" : "Home",
        item: `${SITE_URL}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: page.title.replace(` - ${SITE_NAME}`, ""),
        item: url,
      },
    ],
  };
}
